/**
 * Profile Service - API Client for Instagram Profiles
 *
 * Manages user profile data via the backend API.
 * Profile is set once via the Instagram Profile Modal and reused
 * across all carousel projects.
 *
 * All operations go through the backend API (no direct Supabase calls).
 */

import { apiGet, apiPost } from '@/services/apiClient';
import { Profile, AvatarData, Language } from '@/types';
import type { CachedSuggestions } from '@/services/geminiService';

// ============================================================================
// TYPES
// ============================================================================

export interface ProfilePreferences {
  // Instagram metadata
  biography?: string;
  followersCount?: number;
  followsCount?: number;
  postsCount?: number;
  verified?: boolean;
  isBusinessAccount?: boolean;
  externalUrl?: string;
  // Target audience / avatar (Conteúdo Notável methodology)
  avatarData?: AvatarData;
  // AI-generated content suggestions cache (24h TTL, client-side validated)
  contentSuggestions?: CachedSuggestions;
  // UI language (synced with LanguageContext)
  language?: Language;
  // Onboarding tour state (synced with localStorage as primary, Supabase as backup)
  onboardingState?: import('@/types').OnboardingState;
}

export type UserProfile = Profile & {
  preferences?: ProfilePreferences;
};

// ============================================================================
// GET PROFILE
// ============================================================================

/**
 * Fetches the user's profile via the backend API.
 * The backend identifies the user from the Bearer token.
 * userId is kept as parameter for interface compatibility but not sent to API.
 */
export const getProfile = async (userId: string): Promise<UserProfile | null> => {
  if (!userId) {
    return null;
  }

  try {
    const data = await apiGet<UserProfile>('/api/profile');
    return data || null;
  } catch {
    return null;
  }
};

// ============================================================================
// SAVE PROFILE
// ============================================================================

/**
 * Saves/updates the user's profile via the backend API.
 * userId is kept as parameter for interface compatibility but not sent to API.
 * Returns the confirmed saved profile from the backend.
 */
export const saveProfile = async (
  userId: string,
  profile: Profile,
  preferences?: ProfilePreferences
): Promise<UserProfile> => {
  if (!userId) throw new Error('User not authenticated');

  const saved = await apiPost<UserProfile>('/api/profile', { profile, preferences });

  // Verify the backend confirmed the save
  if (!saved || !saved.handle) {
    throw new Error('Profile save failed: backend did not confirm persistence');
  }

  return saved;
};

// ============================================================================
// HAS PROFILE
// ============================================================================

/**
 * Checks if the user has a complete profile (handle is set).
 * Returns false if profile doesn't exist or handle is empty.
 */
export const hasProfile = async (userId: string): Promise<boolean> => {
  const profile = await getProfile(userId);
  return !!(profile && profile.handle && profile.handle.trim().length > 0);
};
